"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { brand } from "@/lib/brand";

const LOCALE_COOKIE = "NEXT_LOCALE";
const DISMISS_COOKIE = "lang_banner_dismissed";
const ONE_YEAR = 60 * 60 * 24 * 365;

// Visibility is decided server-side by the locale layout (it reads the cookies),
// so this component renders only when it should be shown — no client effect, no
// post-hydration flash. The local `hidden` state just handles an instant dismiss.
export default function LanguageBanner() {
  const locale = useLocale();
  const t = useTranslations("languageBanner");
  const pathname = usePathname();
  const router = useRouter();
  const [hidden, setHidden] = useState(false);

  // Boutique Stays is English-only — the banner is never needed.
  if (brand.id === "boutique-stays") return null;

  if (hidden) return null;

  const other = locale === "ar" ? "en" : "ar";
  // The invite text is in the OTHER language, so render it in that direction.
  const otherDir = other === "ar" ? "rtl" : "ltr";

  function accept() {
    document.cookie = `${LOCALE_COOKIE}=${other}; path=/; max-age=${ONE_YEAR}`;
    router.replace(pathname, { locale: other });
  }

  function dismiss() {
    document.cookie = `${DISMISS_COOKIE}=1; path=/; max-age=${ONE_YEAR}`;
    setHidden(true);
  }

  return (
    <div
      dir={otherDir}
      className="flex items-center justify-center gap-3 bg-ss-blue-50 text-ss-text text-sm px-4 py-2 border-b border-ss-line"
    >
      <span>{t("prompt")}</span>
      <button
        type="button"
        onClick={accept}
        className="font-semibold text-ss-blue hover:underline"
      >
        {t("cta")}
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("dismiss")}
        className="ms-2 text-ss-soft hover:text-ss-text"
      >
        ✕
      </button>
    </div>
  );
}
